import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteConfig, categories } from '@/mocks/homeData';
import { articleDetail, relatedArticles, comments, faqData } from '@/mocks/articleData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: siteConfig.url,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: articleDetail.category,
        item: `${siteConfig.url}/category/${encodeURIComponent(articleDetail.category)}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: articleDetail.title,
        item: `${siteConfig.url}/article/${slug}`,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleDetail.title,
    description: articleDetail.content[0]?.text || '',
    author: {
      '@type': 'Person',
      name: articleDetail.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
      url: siteConfig.url,
    },
    datePublished: articleDetail.date,
    dateModified: articleDetail.updatedAt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.url}/article/${slug}`,
    },
    image: articleDetail.image,
    articleSection: articleDetail.category,
    wordCount: articleDetail.content.reduce((acc, c) => acc + (c.type === 'paragraph' ? c.text.length : 0), 0),
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.mainEntity.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  useEffect(() => {
    const scripts: HTMLScriptElement[] = [];
    const schemas = [breadcrumbSchema, articleSchema, faqSchema];
    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(script);
    });

    return () => {
      scripts.forEach((s) => {
        if (document.head.contains(s)) document.head.removeChild(s);
      });
    };
  }, [slug]);

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24">
        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-screen-xl mx-auto">
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-foreground-800 transition-colors duration-200">
                    首页
                  </Link>
                </li>
                <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                <li>
                  <Link
                    to="/"
                    className="hover:text-foreground-800 transition-colors duration-200"
                  >
                    {articleDetail.category}
                  </Link>
                </li>
                <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                <li className="text-foreground-800 font-medium max-w-[200px] truncate">{articleDetail.title}</li>
              </ol>
            </nav>

            <article className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100/80 text-accent-800 text-xs font-label mb-4">
                  {articleDetail.category}
                </span>

                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-black text-foreground-950 leading-tight mb-4">
                  {articleDetail.title}
                </h1>

                <div className="flex items-center gap-4 mb-6 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <img
                      src={articleDetail.authorAvatar}
                      alt={articleDetail.author}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    {articleDetail.author}
                  </span>
                  <span><i className="ri-calendar-line mr-1"></i>{articleDetail.date}</span>
                  <span><i className="ri-eye-line mr-1"></i>{(articleDetail.views / 10000).toFixed(1)}万</span>
                  <span><i className="ri-time-line mr-1"></i>{articleDetail.readTime}</span>
                </div>

                <div className="rounded-lg overflow-hidden mb-8">
                  <img
                    src={articleDetail.image}
                    alt={articleDetail.title}
                    className="w-full h-[300px] md:h-[420px] object-cover object-top"
                    loading="eager"
                  />
                </div>

                <div className="space-y-5">
                  {articleDetail.content.map((block, idx) => {
                    if (block.type === 'heading') {
                      return (
                        <h2
                          key={idx}
                          className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8"
                        >
                          {block.text}
                        </h2>
                      );
                    }
                    return (
                      <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                        {block.text}
                      </p>
                    );
                  })}
                </div>

                <div className="mt-8 pt-6 border-t border-foreground-200/40">
                  <div className="flex flex-wrap gap-2">
                    {articleDetail.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-12">
                  <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                    精彩评论
                  </h3>
                  <div className="space-y-5">
                    {comments.map((comment) => (
                      <div key={comment.id} className="flex gap-3">
                        <img
                          src={comment.avatar}
                          alt={comment.name}
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-foreground-800">{comment.name}</span>
                            <span className="text-xs text-foreground-400">{comment.time}</span>
                          </div>
                          <p className="text-sm text-foreground-600 leading-relaxed">{comment.content}</p>
                          <div className="flex items-center gap-1 mt-2 text-xs text-foreground-400">
                            <i className="ri-thumb-up-line"></i>
                            <span>{comment.likes}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <aside className="lg:col-span-1">
                <div className="sticky top-24 space-y-8">
                  <div className="rounded-xl bg-secondary-900 p-5 md:p-6 text-background-50">
                    <span className="text-xs text-secondary-300 uppercase tracking-wider">热门推荐</span>
                    <h4 className="font-heading text-lg font-bold mt-2 mb-4">
                      你可能还喜欢
                    </h4>
                    <div className="space-y-4">
                      {relatedArticles.map((art) => (
                        <Link
                          key={art.id}
                          to="/"
                          className="group flex gap-3 cursor-pointer"
                        >
                          <img
                            src={art.image}
                            alt={art.title}
                            className="w-20 h-16 rounded-md object-cover flex-shrink-0"
                          />
                          <div>
                            <h5 className="text-sm font-medium leading-snug group-hover:text-accent-300 transition-colors duration-200 line-clamp-2">
                              {art.title}
                            </h5>
                            <span className="text-xs text-secondary-300 mt-1">
                              <i className="ri-eye-line mr-1"></i>
                              {(art.views / 10000).toFixed(1)}万
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-foreground-200/40 p-5 md:p-6 bg-background-50">
                    <h4 className="font-heading text-base font-bold text-foreground-900 mb-4">
                      热门分类
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((cat) => (
                        <Link
                          key={cat.slug}
                          to="/"
                          className="px-3 py-1.5 rounded-full border border-foreground-200/50 text-xs text-foreground-600 hover:border-accent-300 hover:text-accent-600 transition-all duration-200"
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </article>

            <section className="mt-16 md:mt-20 py-10 md:py-14 px-4 md:px-8 rounded-2xl bg-foreground-950 text-background-50">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-6">
                常见问题
              </h3>
              <div className="space-y-6">
                {faqData.mainEntity.map((item, idx) => (
                  <div key={idx}>
                    <h4 className="text-base font-label font-semibold text-background-50 mb-2">
                      {item.question}
                    </h4>
                    <p className="text-sm text-foreground-400 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}